<template>
  <div class="container mx-auto px-4 py-6">
    <div class="max-w-6xl mx-auto">
      <h1 class="text-3xl font-semibold text-center text-gray-800 mb-6">Aprobación de usuarios</h1>

      <!-- Toolbar -->
      <div class="bg-white shadow-md rounded-lg p-4 mb-6">
        <div class="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div class="flex-1 flex flex-col sm:flex-row gap-3">
            <div class="flex-1">
              <input
                v-model="q"
                type="text"
                class="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Buscar por nombre, email, documento, empresa…"
              />
            </div>

            <select v-model="categoryFilter" class="border rounded-lg px-3 py-2">
              <option value="all">Todas las categorías</option>
              <option value="Asistente">{{ labels.Asistente }}</option>
              <option value="Conferencista">{{ labels.Conferencista }}</option>
              <option value="Staff">{{ labels.Staff }}</option>
            </select>
          </div>

          <div class="flex flex-wrap gap-2 items-center justify-between">
            <label class="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" v-model="showApproved" />
              Mostrar aprobados
            </label>

            <label class="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" v-model="onlyIncomplete" />
              Solo incompletos
            </label>

            <button
              class="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg"
              @click="fetchMembers"
              :disabled="loading"
            >
              {{ loading ? "Cargando..." : "Refrescar" }}
            </button>

            <button
              class="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-4 py-2 rounded-lg"
              @click="expandAll"
              :disabled="loading || filteredGroups.length === 0"
              title="Abre todos los grupos y muestra el detalle de todos los usuarios visibles"
            >
              Expandir todo
            </button>

            <button
              class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-4 py-2 rounded-lg"
              @click="collapseAll"
              :disabled="loading"
              title="Cierra grupos y oculta detalles"
            >
              Colapsar todo
            </button>
          </div>
        </div>

        <!-- Resumen -->
        <div class="mt-3 flex flex-wrap gap-2 text-sm text-gray-700">
          <span class="px-2 py-1 rounded bg-gray-100">Grupos: <b>{{ totalGroups }}</b></span>

          <span class="px-2 py-1 rounded bg-gray-100">
            Usuarios visibles: <b>{{ totalVisibleUsers }}</b>
          </span>

          <span class="px-2 py-1 rounded bg-green-100 text-green-800">
            Completos: <b>{{ totalCompleteVisible }}</b> / <b>{{ totalVisibleUsers }}</b>
          </span>

          <span class="px-2 py-1 rounded bg-yellow-100 text-yellow-800">
            Pendientes: <b>{{ totalPending }}</b>
          </span>

          <span class="px-2 py-1 rounded bg-blue-100 text-blue-800">
            Incompletos: <b>{{ totalIncompleteVisible }}</b>
          </span>
        </div>
      </div>

      <!-- Content -->
      <div v-if="loading" class="text-center text-gray-600 py-10">Cargando usuarios…</div>

      <div v-else>
        <div v-if="filteredGroups.length === 0" class="bg-white border rounded-lg p-6 text-center text-gray-600">
          No hay usuarios para mostrar con los filtros actuales.
        </div>

        <div
          v-for="g in filteredGroups"
          :key="g.groupId"
          class="mb-5 bg-white shadow-md rounded-lg overflow-hidden"
        >
          <!-- Group header -->
          <div class="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50">
            <!-- Click en el bloque izquierdo = toggle -->
            <button class="flex-1 text-left" @click="toggleGroup(g.groupId)">
              <div class="text-lg font-semibold text-gray-800">
                Grupo: {{ g?.datadelgrupo?.name || g.groupId }}
              </div>

              <div class="text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  Completos: <b>{{ groupCompleteCount(g) }}</b> / <b>{{ groupTotalCount(g) }}</b>
                </span>
                <span>
                  Pendientes (visibles): <b>{{ groupPendingCount(g) }}</b>
                </span>
                <span>
                  Visibles: <b>{{ groupVisibleCount(g) }}</b>
                </span>
              </div>
            </button>

            <!-- Acciones de grupo -->
            <div class="flex items-center gap-2 ml-3">
              <button
                class="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
                @click.stop="expandGroup(g)"
                :disabled="groupVisibleCount(g) === 0"
                title="Abre el grupo y despliega el detalle de todos los usuarios visibles"
              >
                Expandir grupo
              </button>

              <button
                class="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
                @click.stop="collapseGroup(g)"
                title="Cierra el grupo y oculta sus detalles"
              >
                Colapsar grupo
              </button>

              <span
                class="text-xs px-2 py-1 rounded-full"
                :class="groupPendingCount(g) ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'"
              >
                {{ groupPendingCount(g) ? "Pendiente" : "OK" }}
              </span>

              <button class="p-2" @click.stop="toggleGroup(g.groupId)" aria-label="Toggle grupo">
                <svg
                  class="w-5 h-5 text-gray-500 transition-transform"
                  :class="isGroupOpen(g.groupId) ? 'rotate-180' : ''"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Group body -->
          <div v-if="isGroupOpen(g.groupId)" class="px-4 pb-4">
            <!-- categorías ya filtradas (sin v-if con v-for) -->
            <div v-for="cat in categoriesForGroup(g)" :key="cat.key" class="mt-4">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 class="text-base font-semibold text-gray-700">
                    {{ cat.title }}
                  </h3>

                  <div class="text-sm text-gray-500">
                    Completos: <b>{{ countComplete(g, cat.key) }}</b> / <b>{{ countTotal(g, cat.key) }}</b>
                    · Pendientes: <b>{{ countPending(g, cat.key) }}</b>
                    · Visibles: <b>{{ usersForGroupCategory(g, cat.key).length }}</b>
                  </div>
                </div>

                <div class="flex flex-wrap gap-2 items-center">
                  <button
                    v-if="countPending(g, cat.key) > 0"
                    class="text-sm px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white"
                    @click="approveManyVisible(g, cat.key)"
                  >
                    Aprobar visibles
                  </button>
                </div>
              </div>

              <div class="mt-2 space-y-3">
                <div
                  v-for="u in usersForGroupCategory(g, cat.key)"
                  :key="u.staff_id"
                  class="border rounded-lg p-3 hover:shadow-sm transition"
                >
                  <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <!-- left -->
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <div class="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold">
                          {{ initials(u) }}
                        </div>
                        <div class="min-w-0">
                          <div class="font-semibold text-gray-800 truncate">
                            {{ (u.nombres || "").trim() }} {{ (u.apellidos || "").trim() }}
                          </div>
                          <div class="text-sm text-gray-600 truncate">
                            {{ u.correo_electronico || "—" }}
                          </div>
                        </div>
                      </div>

                      <div class="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-700">
                        <div class="sm:col-span-3 lg:col-span-1">
                          <div><span class="text-gray-500">Documento:</span> <b>{{ u.numero_de_documento || "—" }}</b></div>

                          <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div class="rounded-md border border-blue-100 bg-blue-50/70 px-2.5 py-2">
                              <div class="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Creado</div>
                              <div class="text-xs sm:text-sm text-gray-800 font-medium mt-0.5">
                                {{ formatDateTime(u.creado || u.created_at) || "—" }}
                              </div>
                            </div>

                            <div class="rounded-md border border-blue-100 bg-blue-50/70 px-2.5 py-2">
                              <div class="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Actualizado</div>
                              <div class="text-xs sm:text-sm text-gray-800 font-medium mt-0.5">
                                {{ formatDateTime(u.editado || u.updated_at) || "—" }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div><span class="text-gray-500">Empresa:</span> <b>{{ u.empresa || "—" }}</b></div>
                        <div><span class="text-gray-500">Tel:</span> <b>{{ u.telefono || "—" }}</b></div>
                      </div>
                    </div>

                    <!-- right -->
                    <div class="flex md:flex-col gap-2 md:items-end">
                      <span
                        class="text-xs px-2 py-1 rounded-full"
                        :class="isApproved(u) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                      >
                        {{ isApproved(u) ? "Aprobado" : "Pendiente" }}
                      </span>

                      <span
                        class="text-xs px-2 py-1 rounded-full"
                        :class="isUserComplete(u) ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'"
                        :title="missingFields(u).join(', ')"
                      >
                        {{ isUserComplete(u) ? "Completo" : `Incompleto (${missingFields(u).length})` }}
                      </span>

                      <div class="flex gap-2">
                        <button
                          class="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
                          @click="toggleUser(u.staff_id)"
                        >
                          {{ isUserOpen(u.staff_id) ? "Ocultar" : "Ver" }}
                        </button>

                        <button
                          v-if="!isApproved(u)"
                          class="text-sm px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                          :disabled="!!approving[u.staff_id]"
                          @click="approveUser(u.staff_id)"
                        >
                          {{ approving[u.staff_id] ? "Aprobando…" : "Aprobar" }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- detail -->
                  <div v-if="isUserOpen(u.staff_id)" class="mt-3 border-t pt-3">
                    <div class="text-xs text-gray-500 mb-2">Detalle (campos activos del evento)</div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div
                        v-for="f in activeFieldsForDisplay"
                        :key="f.label"
                        class="flex items-start justify-between gap-3 rounded-md px-3 py-2"
                        :class="fieldIsMissingRequired(u, f) ? 'bg-red-50' : 'bg-gray-50'"
                      >
                        <div class="text-sm text-gray-600">
                          {{ fieldName(f) }}
                          <span v-if="isRequired(f)" class="text-xs text-red-600 ml-1">*</span>
                        </div>

                        <div class="text-sm text-gray-900 text-right break-words max-w-[60%]">
                          <template v-if="(f.tipo_ui || 'input') === 'multicheck'">
                            <div class="flex flex-wrap gap-1 justify-end">
                              <span
                                v-for="chip in multiChips(u[f.label])"
                                :key="chip"
                                class="px-2 py-0.5 rounded-full bg-gray-200 text-gray-800 text-xs"
                              >
                                {{ chip }}
                              </span>
                              <span v-if="multiChips(u[f.label]).length === 0" class="text-gray-400">—</span>
                            </div>
                          </template>

                          <template v-else>
                            {{ normalizeVal(u[f.label]) || "—" }}
                          </template>
                        </div>
                      </div>

                      <div class="flex items-start justify-between gap-3 rounded-md px-3 py-2 bg-gray-50">
                        <div class="text-sm text-gray-600">ID</div>
                        <div class="text-sm text-gray-900">{{ u.staff_id || "—" }}</div>
                      </div>
                      <div class="flex items-start justify-between gap-3 rounded-md px-3 py-2 bg-gray-50">
                        <div class="text-sm text-gray-600">Autorizado</div>
                        <div class="text-sm text-gray-900">{{ u.autorizado || "0" }}</div>
                      </div>

                      <div
                        v-if="u.staff_id"
                        class="md:col-span-2 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-3"
                      >
                        <div class="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-2">
                          Fechas de carga
                        </div>

                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div class="rounded-md bg-white/80 px-3 py-2 border border-blue-100">
                            <div class="text-xs text-blue-700 font-medium mb-1">Creado</div>
                            <div class="text-sm text-gray-900 font-semibold">
                              {{ formatDateTime(u.creado || u.created_at) || "—" }}
                            </div>
                            <div class="text-xs text-gray-500 mt-1">Momento en que se registró este usuario.</div>
                          </div>

                          <div class="rounded-md bg-white/80 px-3 py-2 border border-blue-100">
                            <div class="text-xs text-blue-700 font-medium mb-1">Actualizado</div>
                            <div class="text-sm text-gray-900 font-semibold">
                              {{ formatDateTime(u.editado || u.updated_at) || "—" }}
                            </div>
                            <div class="text-xs text-gray-500 mt-1">Último cambio detectado en el registro.</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div><!-- category -->
          </div><!-- group body -->
        </div><!-- group -->
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";

const humanize = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (m) => m.toUpperCase());

const sortConfiguredOrder = (list = []) => {
  return (list || [])
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const ao = Number(a.item?.orden);
      const bo = Number(b.item?.orden);
      const aHas = Number.isFinite(ao) && ao > 0;
      const bHas = Number.isFinite(bo) && bo > 0;

      if (aHas && bHas && ao !== bo) return ao - bo;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return a.idx - b.idx;
    })
    .map(({ item }, idx) => ({ ...item, orden: idx + 1 }));
};

export default {
  name: "ApproveUsers",
  props: { eventoToken: { type: String, required: true } },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      event: {},
      camposMeta: [],
      requiredMap: {},
      loading: true,

      q: "",
      showApproved: false,
      onlyIncomplete: false,
      categoryFilter: "all",

      openGroups: {},
      openUsers: {},
      approving: {},

      // cache por grupo/categoría (filtrado)
      cache: {},

      labels: {
        Staff: "Staff",
        Asistente: "Asistentes",
        Conferencista: "Expositores",
      },
    };
  },

  computed: {
    totalGroups() {
      return Object.keys(this.event || {}).length;
    },

    categories() {
      return [
        { key: "Asistente", title: this.labels.Asistente },
        { key: "Conferencista", title: this.labels.Conferencista },
        { key: "Staff", title: this.labels.Staff },
      ];
    },

    visibleCategories() {
      if (this.categoryFilter === "all") return this.categories;
      return this.categories.filter((c) => c.key === this.categoryFilter);
    },

    groupsArray() {
      return Object.entries(this.event || {}).map(([groupId, g]) => ({
        groupId,
        ...g,
      }));
    },

    filteredGroups() {
      return this.groupsArray.filter((g) => this.groupVisibleCount(g) > 0);
    },

    activeFieldsForDisplay() {
      const active = (this.camposMeta || []).filter((f) => f && f.activo && f.label);
      return sortConfiguredOrder(active);
    },

    totalVisibleUsers() {
      let n = 0;
      this.filteredGroups.forEach((g) => {
        this.visibleCategories.forEach((cat) => {
          n += this.usersForGroupCategory(g, cat.key).length;
        });
      });
      return n;
    },

    totalCompleteVisible() {
      let n = 0;
      this.filteredGroups.forEach((g) => {
        this.visibleCategories.forEach((cat) => {
          n += this.usersForGroupCategory(g, cat.key).filter((u) => this.isUserComplete(u)).length;
        });
      });
      return n;
    },

    totalPending() {
      let n = 0;
      this.filteredGroups.forEach((g) => {
        this.visibleCategories.forEach((cat) => {
          n += this.usersForGroupCategory(g, cat.key).filter((u) => !this.isApproved(u)).length;
        });
      });
      return n;
    },

    totalIncompleteVisible() {
      let n = 0;
      this.filteredGroups.forEach((g) => {
        this.visibleCategories.forEach((cat) => {
          n += this.usersForGroupCategory(g, cat.key).filter((u) => !this.isUserComplete(u)).length;
        });
      });
      return n;
    },
  },

  watch: {
    q() { this.cache = {}; },
    showApproved() { this.cache = {}; },
    onlyIncomplete() { this.cache = {}; },
    categoryFilter() { this.cache = {}; },
    event: {
      deep: true,
      handler() { this.cache = {}; }
    }
  },

  mounted() {
    this.fetchMembers();
  },

  methods: {
    async fetchMembers() {
      this.loading = true;
      try {
        const response = await axios.post("/api/list/members-staff", { token_evento: this.eventoToken });

        this.event = response.data?.evento || {};

        const metaRaw = response.data?.campos_obligatorios || "[]";
        const meta = JSON.parse(metaRaw);

        this.camposMeta = (meta || []).map((campo) => ({
          label: campo.label,
          activo: !!campo.activo,
          requerido: !!campo.requerido,
          mandatorio: !!campo.mandatorio,
          custom: !!campo.custom,
          nombre_del_campo: campo.nombre_del_campo || "",
          tipo_ui: campo.tipo_ui || "input",
          opciones: Array.isArray(campo.opciones) ? campo.opciones : [],
          orden: Number(campo.orden) > 0 ? Number(campo.orden) : null,
        }));

        this.requiredMap = (this.camposMeta || []).reduce((acc, f) => {
          if (f.activo && (f.requerido || f.mandatorio) && f.label) acc[f.label] = true;
          return acc;
        }, {});

        this.cache = {};

        const first = Object.keys(this.event || {})[0];
        if (first && this.openGroups[first] === undefined) this.openGroups[first] = true;
      } catch (e) {
        console.error("Error fetching members:", e);
      } finally {
        this.loading = false;
      }
    },

    // categorías visibles que de verdad tienen usuarios visibles
    categoriesForGroup(g) {
      return this.visibleCategories.filter((cat) => this.usersForGroupCategory(g, cat.key).length > 0);
    },

    // -------- Expand / Collapse ----------
    expandAll() {
      const openGroups = {};
      const openUsers = { ...this.openUsers };

      this.filteredGroups.forEach((g) => {
        openGroups[g.groupId] = true;

        this.visibleCategories.forEach((cat) => {
          this.usersForGroupCategory(g, cat.key).forEach((u) => {
            if (u?.staff_id) openUsers[u.staff_id] = true;
          });
        });
      });

      this.openGroups = openGroups;
      this.openUsers = openUsers;
    },

    collapseAll() {
      this.openGroups = {};
      this.openUsers = {};
    },

    expandGroup(g) {
      const gid = g.groupId;
      const openGroups = { ...this.openGroups, [gid]: true };
      const openUsers = { ...this.openUsers };

      this.visibleCategories.forEach((cat) => {
        this.usersForGroupCategory(g, cat.key).forEach((u) => {
          if (u?.staff_id) openUsers[u.staff_id] = true;
        });
      });

      this.openGroups = openGroups;
      this.openUsers = openUsers;
    },

    collapseGroup(g) {
      const gid = g.groupId;

      // cierra grupo
      const nextGroups = { ...this.openGroups };
      delete nextGroups[gid];

      // cierra detalles SOLO de usuarios de ese grupo (para no afectar otros)
      const nextUsers = { ...this.openUsers };
      (this.usersRaw(g) || []).forEach((u) => {
        if (u?.staff_id) delete nextUsers[u.staff_id];
      });

      this.openGroups = nextGroups;
      this.openUsers = nextUsers;
    },

    toggleGroup(groupId) {
      this.openGroups = { ...this.openGroups, [groupId]: !this.openGroups[groupId] };
    },
    isGroupOpen(groupId) {
      return !!this.openGroups[groupId];
    },

    toggleUser(staffId) {
      this.openUsers = { ...this.openUsers, [staffId]: !this.openUsers[staffId] };
    },
    isUserOpen(staffId) {
      return !!this.openUsers[staffId];
    },

    // -------- helpers ----------
    _str(v) {
      if (v === null || v === undefined) return "";
      return String(v).trim();
    },

    _qMatch(user) {
      const q = this._str(this.q).toLowerCase();
      if (!q) return true;

      const hay = [
        user?.nombres,
        user?.apellidos,
        user?.correo_electronico,
        user?.numero_de_documento,
        user?.empresa,
        user?.telefono,
      ]
        .map((x) => this._str(x).toLowerCase())
        .join(" | ");

      return hay.includes(q);
    },

    isApproved(u) {
      return String(u?.autorizado ?? "") === "1";
    },

    isRequired(f) {
      return !!this.requiredMap?.[f.label];
    },

    fieldIsMissingRequired(user, f) {
      if (!this.isRequired(f)) return false;
      return this._str(user?.[f.label]) === "";
    },

    missingFields(user) {
      const req = Object.keys(this.requiredMap || {});
      const miss = [];
      req.forEach((label) => {
        if (this._str(user?.[label]) === "") {
          const meta = (this.camposMeta || []).find((x) => x.label === label);
          miss.push((meta?.nombre_del_campo || humanize(label)).trim());
        }
      });
      return miss;
    },

    isUserComplete(user) {
      return this.missingFields(user).length === 0;
    },

    initials(u) {
      const n = this._str(u?.nombres);
      const a = this._str(u?.apellidos);
      const s = (n[0] || "") + (a[0] || "");
      return (s || "U").toUpperCase();
    },

    normalizeVal(v) {
      if (v === null || v === undefined) return "";
      return String(v).trim();
    },

    formatDateTime(value) {
      const raw = this._str(value);
      if (!raw) return "";

      const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
      const date = new Date(normalized);

      if (Number.isNaN(date.getTime())) return raw;

      return new Intl.DateTimeFormat("es-MX", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },

    multiChips(v) {
      const txt = this._str(v);
      if (!txt) return [];
      return txt.split(",").map((x) => x.trim()).filter(Boolean);
    },

    fieldName(f) {
      return (f.nombre_del_campo || humanize(f.label)).trim();
    },

    usersRaw(g) {
      return g?.staffs?.usuarios || [];
    },

    // cacheado por grupo/categoría (con filtros)
    usersForGroupCategory(g, categoryKey) {
      const gid = g.groupId;
      if (!this.cache[gid]) this.cache[gid] = {};
      if (this.cache[gid][categoryKey]) return this.cache[gid][categoryKey];

      const users = this.usersRaw(g).filter((u) => u?.escarapela === categoryKey);

      let out = users.filter(this._qMatch);
      if (!this.showApproved) out = out.filter((u) => !this.isApproved(u));
      if (this.onlyIncomplete) out = out.filter((u) => !this.isUserComplete(u));

      this.cache[gid][categoryKey] = out;
      return out;
    },

    // ----- conteos por grupo (globales, no “visibles”) -----
    groupTotalCount(g) {
      return this.usersRaw(g).length;
    },
    groupCompleteCount(g) {
      return this.usersRaw(g).filter((u) => this.isUserComplete(u)).length;
    },

    // ----- conteos por categoría (globales) -----
    countTotal(g, categoryKey) {
      return this.usersRaw(g).filter((u) => u?.escarapela === categoryKey).length;
    },
    countPending(g, categoryKey) {
      return this.usersRaw(g).filter((u) => u?.escarapela === categoryKey && !this.isApproved(u)).length;
    },
    countComplete(g, categoryKey) {
      return this.usersRaw(g).filter((u) => u?.escarapela === categoryKey && this.isUserComplete(u)).length;
    },

    // ----- visibles -----
    groupVisibleCount(g) {
      return this.visibleCategories.reduce((acc, c) => acc + this.usersForGroupCategory(g, c.key).length, 0);
    },
    groupPendingCount(g) {
      let n = 0;
      this.visibleCategories.forEach((c) => {
        n += this.usersForGroupCategory(g, c.key).filter((u) => !this.isApproved(u)).length;
      });
      return n;
    },

    // -------- approve ----------
    async approveUser(staffId) {
      if (!staffId) return;
      this.approving = { ...this.approving, [staffId]: true };

      try {
        const response = await axios.post("/api/aprovation/staffs/check", {
          staff_member: staffId,
          Authorization: this.$store.state.user.token,
        });

        if (response.data?.success) {
          this.toast.success("Usuario aprobado correctamente.");
          this.updateStaffMember(staffId, { autorizado: "1" });
        } else {
          this.toast.error("No se pudo aprobar el usuario.");
        }
      } catch (e) {
        console.error("Error al aprobar el staff:", e);
        this.toast.error("Error al aprobar al usuario.");
      } finally {
        const next = { ...this.approving };
        delete next[staffId];
        this.approving = next;
      }
    },

    async approveManyVisible(group, categoryKey) {
      const list = this.usersForGroupCategory(group, categoryKey)
        .filter((u) => !this.isApproved(u))
        .map((u) => u.staff_id)
        .filter(Boolean);

      if (!list.length) return;

      const CONCURRENCY = 5;
      let i = 0;

      const workers = Array.from({ length: Math.min(CONCURRENCY, list.length) }).map(async () => {
        while (i < list.length) {
          const id = list[i++];
          await this.approveUser(id);
        }
      });

      await Promise.all(workers);
    },

    updateStaffMember(staffId, updates) {
      const eventCopy = { ...(this.event || {}) };

      Object.keys(eventCopy).forEach((groupId) => {
        const usuarios = eventCopy[groupId]?.staffs?.usuarios || [];
        const idx = usuarios.findIndex((u) => u.staff_id === staffId);
        if (idx !== -1) {
          usuarios.splice(idx, 1, { ...usuarios[idx], ...updates });
        }
      });

      this.event = eventCopy;
      this.cache = {};
    },
  },
};
</script>

<style scoped>
/* Tailwind hace casi todo */
</style>
